import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  FileText, Download, Eye, Trash2, RefreshCw, Loader2, 
  Plus, Upload, Edit, CheckCircle2, AlertCircle, Settings, Cloud, CloudOff
} from 'lucide-react';
import { formatDateDisplay } from '../utils/dateUtils';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const StandardDocuments = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [systemFields, setSystemFields] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState('');
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState('');
  
  // Company assets state
  const [companyAssets, setCompanyAssets] = useState({ stamp: null, signature: null });
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [stampWidth, setStampWidth] = useState(1.0);
  const [signatureWidth, setSignatureWidth] = useState(1.5);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    document_type: '',
    description: '',
    is_bilingual: false,
    target: 'tap',
    required_fields: [],
    system_fields: [],
    custom_fields: [],
    file: null
  });

  // Custom field being added
  const [newCustomField, setNewCustomField] = useState({
    field_name: '',
    label: '',
    field_type: 'text',
    predefined_values: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch fields when target changes
    fetchFieldsForTarget(formData.target);
  }, [formData.target]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, typesRes, fieldsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/document-templates/templates?include_inactive=true`, { headers: getAuthHeader() }),
        axios.get(`${API}/document-templates/document-types`, { headers: getAuthHeader() }),
        axios.get(`${API}/document-templates/available-fields?target=tap`, { headers: getAuthHeader() }),
        axios.get(`${API}/document-templates/company-assets`, { headers: getAuthHeader() }).catch(() => ({ data: { stamp: null, signature: null } }))
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setDocumentTypes(typesRes.data.types || []);
      setAvailableFields(fieldsRes.data.fields || []);
      setSystemFields(fieldsRes.data.system_fields || []);
      setCompanyAssets(assetsRes.data || { stamp: null, signature: null });
      
      // Load saved widths
      if (assetsRes.data?.stamp?.width_inches) {
        setStampWidth(assetsRes.data.stamp.width_inches);
      }
      if (assetsRes.data?.signature?.width_inches) {
        setSignatureWidth(assetsRes.data.signature.width_inches);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldsForTarget = async (target) => {
    try {
      const fieldsRes = await axios.get(`${API}/document-templates/available-fields?target=${target}`, { headers: getAuthHeader() });
      setAvailableFields(fieldsRes.data.fields || []);
      setSystemFields(fieldsRes.data.system_fields || []);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const handleUploadStamp = async (file) => {
    if (!file) return;
    setUploadingStamp(true);
    try {
      const formData = new FormData();
      formData.append('stamp_file', file);
      formData.append('width_inches', stampWidth);
      await axios.post(`${API}/document-templates/company-assets/stamp`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Company stamp uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload stamp'));
    } finally {
      setUploadingStamp(false);
    }
  };

  const handleUploadSignature = async (file) => {
    if (!file) return;
    setUploadingSignature(true);
    try {
      const formData = new FormData();
      formData.append('signature_file', file);
      formData.append('width_inches', signatureWidth);
      await axios.post(`${API}/document-templates/company-assets/signature`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Company signature uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload signature'));
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleUpdateStampSize = async () => {
    try {
      await axios.put(`${API}/document-templates/company-assets/stamp/size?width_inches=${stampWidth}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Stamp size updated successfully');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update stamp size'));
    }
  };

  const handleUpdateSignatureSize = async () => {
    try {
      await axios.put(`${API}/document-templates/company-assets/signature/size?width_inches=${signatureWidth}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Signature size updated successfully');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update signature size'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_ar: '',
      document_type: '',
      description: '',
      is_bilingual: false,
      target: 'tap',
      required_fields: [],
      system_fields: [],
      custom_fields: [],
      file: null
    });
    setNewCustomField({
      field_name: '',
      label: '',
      field_type: 'text',
      predefined_values: ''
    });
  };

  const handleAddTemplate = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name || '',
      name_ar: template.name_ar || '',
      document_type: template.document_type || '',
      description: template.description || '',
      is_bilingual: template.is_bilingual || false,
      target: template.target || 'tap',
      required_fields: template.required_fields || [],
      system_fields: template.system_fields || [],
      custom_fields: template.custom_fields || [],
      file: null
    });
    // Fetch fields for the template's target
    fetchFieldsForTarget(template.target || 'tap');
    setShowEditDialog(true);
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowPreviewDialog(true);
  };

  // Open PDF in viewer dialog
  const openPdfViewer = (url, title, downloadUrl = null) => {
    setPdfViewerUrl(url);
    setPdfViewerTitle(title);
    setPdfDownloadUrl(downloadUrl || url);
    setPdfViewerOpen(true);
  };

  // View template PDF
  const handleViewTemplatePdf = (template) => {
    // Prefer proxying through our backend using the OneDrive file_id — direct
    // shareable URLs require the viewer's Microsoft account to be on the
    // file's access list which surfaces the "You can't access this item"
    // permission page for anyone outside that group. The proxy uses the
    // service credentials so any app-authorized user can view the file.
    const previewUrl = template.onedrive_file_id
      ? `${API}/onedrive/proxy/${template.onedrive_file_id}?filename=${encodeURIComponent(template.original_filename || template.name || 'template.pdf')}&download=false`
      : (template.onedrive_url || `${API}/document-templates/templates/${template.id}/preview`);
    const downloadUrl = template.onedrive_file_id
      ? `${API}/onedrive/proxy/${template.onedrive_file_id}?filename=${encodeURIComponent(template.original_filename || template.name || 'template.pdf')}&download=true`
      : `${API}/document-templates/templates/${template.id}/download`;
    openPdfViewer(previewUrl, template.original_filename || template.name, downloadUrl);
  };

  // View newly selected file
  const handlePreviewNewFile = (file) => {
    const url = URL.createObjectURL(file);
    openPdfViewer(url, file.name, null);
  };

  const handleSaveTemplate = async (isEdit = false) => {
    if (!formData.name || !formData.document_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isEdit && !formData.file) {
      toast.error('Please upload a PDF template file');
      return;
    }

    try {
      setSaving(true);
      
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('document_type', formData.document_type);
      submitData.append('target', formData.target);
      if (formData.name_ar) submitData.append('name_ar', formData.name_ar);
      if (formData.description) submitData.append('description', formData.description);
      submitData.append('is_bilingual', formData.is_bilingual ? 'true' : 'false');
      submitData.append('required_fields', JSON.stringify(formData.required_fields || []));
      submitData.append('system_fields', JSON.stringify(formData.system_fields || []));
      submitData.append('custom_fields', JSON.stringify(formData.custom_fields || []));
      if (formData.file) submitData.append('template_file', formData.file);

      if (isEdit) {
        await axios.put(`${API}/document-templates/templates/${selectedTemplate.id}`, submitData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template updated successfully');
        setShowEditDialog(false);
      } else {
        await axios.post(`${API}/document-templates/templates`, submitData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template created successfully');
        setShowAddDialog(false);
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(formatErrorMessage(error, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/document-templates/templates/${templateId}`, { headers: getAuthHeader() });
      toast.success('Template deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      const submitData = new FormData();
      // Always send boolean as string for consistent parsing
      submitData.append('is_active', !template.is_active ? 'true' : 'false');

      await axios.put(`${API}/document-templates/templates/${template.id}`, submitData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Template ${template.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error(formatErrorMessage(error, 'Failed to update template status'));
    }
  };

  const handleFieldToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      required_fields: prev.required_fields.includes(field)
        ? prev.required_fields.filter(f => f !== field)
        : [...prev.required_fields, field]
    }));
  };

  const handleSystemFieldToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      system_fields: prev.system_fields.includes(field)
        ? prev.system_fields.filter(f => f !== field)
        : [...prev.system_fields, field]
    }));
  };

  const handleAddCustomField = () => {
    if (!newCustomField.field_name || !newCustomField.label) {
      toast.error('Please provide field name and label');
      return;
    }
    
    const customField = {
      field_name: newCustomField.field_name.toLowerCase().replace(/\s+/g, '_'),
      label: newCustomField.label,
      field_type: newCustomField.field_type,
      predefined_values: newCustomField.field_type === 'predefined' 
        ? newCustomField.predefined_values.split(',').map(v => v.trim()).filter(v => v)
        : []
    };
    
    setFormData(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, customField]
    }));
    
    setNewCustomField({
      field_name: '',
      label: '',
      field_type: 'text',
      predefined_values: ''
    });
  };

  const handleRemoveCustomField = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Standard Documents</h2>
          <p className="text-sm text-slate-500">Manage document templates for TAP and Hiring candidates</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={handleAddTemplate} size="sm" className="gap-2">
            <Plus className="w-4 h-4" />
            Add Template
          </Button>
        </div>
      </div>

      {/* Templates List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Document Templates ({templates.length})</CardTitle>
          <CardDescription>Upload Word, Excel, or PDF templates for automatic document generation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Fields</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No templates found. Click "Add Template" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map(template => (
                    <TableRow key={template.id} className={!template.is_active ? 'opacity-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-500" />
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {template.name}
                              {template.onedrive_url ? (
                                <Cloud className="w-3 h-3 text-blue-500" title="Synced to OneDrive" />
                              ) : (
                                <CloudOff className="w-3 h-3 text-slate-400" title="Local only - re-upload to sync" />
                              )}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {documentTypes.find(t => t.id === template.document_type)?.name || template.document_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={template.target === 'hiring' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}
                        >
                          {template.target === 'hiring' ? 'Hiring' : 'TAP'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(template.required_fields || []).slice(0, 2).map(f => (
                            <Badge key={f} variant="secondary" className="text-xs">{f}</Badge>
                          ))}
                          {((template.required_fields || []).length + (template.system_fields || []).length + (template.custom_fields || []).length) > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{(template.required_fields || []).length + (template.system_fields || []).length + (template.custom_fields || []).length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant={template.is_active ? 'default' : 'secondary'}
                          className={template.is_active ? 'bg-green-600' : ''}
                        >
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreviewTemplate(template)}
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditTemplate(template)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleActive(template)}
                            title={template.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {template.is_active ? (
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Company Stamp & Signature */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Company Stamp & Signature
          </CardTitle>
          <CardDescription>Upload company stamp and authorized signature for official documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Stamp */}
            <div className="space-y-3">
              <Label className="font-medium">Company Stamp</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center bg-slate-50">
                {companyAssets?.stamp?.uploaded ? (
                  <div className="space-y-2">
                    <CheckCircle2 className="w-8 h-8 mx-auto text-green-600" />
                    <p className="text-sm text-green-700 font-medium">Stamp Uploaded</p>
                    <p className="text-xs text-slate-500">{companyAssets.stamp.filename}</p>
                    <p className="text-xs text-slate-400">
                      Updated: {formatDateDisplay(companyAssets.stamp.updated_at)}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 mx-auto text-slate-400" />
                    <p className="text-sm text-slate-500">No stamp uploaded</p>
                  </div>
                )}
              </div>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => handleUploadStamp(e.target.files[0])}
                disabled={uploadingStamp}
              />
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Width (inches):</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="3"
                  value={stampWidth}
                  onChange={(e) => setStampWidth(parseFloat(e.target.value) || 1.0)}
                  className="w-20 h-8 text-sm"
                />
                {companyAssets.stamp?.uploaded && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUpdateStampSize}
                    className="h-8 text-xs"
                  >
                    Save Size
                  </Button>
                )}
              </div>
              {uploadingStamp && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </div>
              )}
              <p className="text-xs text-slate-500">Accepted formats: JPEG, PNG (max 5MB). Set width before uploading or click "Save Size" to update.</p>
            </div>

            {/* Authorized Signature */}
            <div className="space-y-3">
              <Label className="font-medium">Authorized Signature</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center bg-slate-50">
                {companyAssets?.signature?.uploaded ? (
                  <div className="space-y-2">
                    <CheckCircle2 className="w-8 h-8 mx-auto text-green-600" />
                    <p className="text-sm text-green-700 font-medium">Signature Uploaded</p>
                    <p className="text-xs text-slate-500">{companyAssets.signature.filename}</p>
                    <p className="text-xs text-slate-400">
                      Updated: {formatDateDisplay(companyAssets.signature.updated_at)}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 mx-auto text-slate-400" />
                    <p className="text-sm text-slate-500">No signature uploaded</p>
                  </div>
                )}
              </div>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => handleUploadSignature(e.target.files[0])}
                disabled={uploadingSignature}
              />
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Width (inches):</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="4"
                  value={signatureWidth}
                  onChange={(e) => setSignatureWidth(parseFloat(e.target.value) || 1.5)}
                  className="w-20 h-8 text-sm"
                />
                {companyAssets.signature?.uploaded && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUpdateSignatureSize}
                    className="h-8 text-xs"
                  >
                    Save Size
                  </Button>
                )}
              </div>
              {uploadingSignature && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </div>
              )}
              <p className="text-xs text-slate-500">Accepted formats: JPEG, PNG (max 5MB). Set width before uploading or click "Save Size" to update.</p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Upload your company stamp and authorized signature here. When generating official documents, 
              you can choose to add these to the PDF output.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Template Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Template' : 'Add New Template'}</DialogTitle>
            <DialogDescription>
              Upload a Word (.docx), Excel (.xlsx), or PDF template with placeholders like {"{{name}}"}, {"{{passport_number}}"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Template Name */}
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Employment Offer Letter"
              />
            </div>

            {/* Document Type */}
            <div className="space-y-2">
              <Label>Document Type *</Label>
              <Select
                value={formData.document_type}
                onValueChange={(value) => setFormData({ ...formData, document_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select document type..." />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Template Target */}
            <div className="space-y-2">
              <Label>Template Target *</Label>
              <Select
                value={formData.target}
                onValueChange={(value) => setFormData({ ...formData, target: value, required_fields: [] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select target..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tap">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-blue-50">TAP</Badge>
                      TAP Candidates (Existing Employees)
                    </div>
                  </SelectItem>
                  <SelectItem value="hiring">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-50">Hiring</Badge>
                      Hiring Candidates (New Recruits)
                    </div>
                  </SelectItem>
                  <SelectItem value="handover">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-amber-50">Handover</Badge>
                      Inventory Handover Form (Motorcycle → Rider)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {formData.target === 'hiring'
                  ? 'Fields will be populated from Hiring candidate data'
                  : formData.target === 'handover'
                  ? 'Fields will be populated from the motorcycle handover record (rider + bike snapshot)'
                  : 'Fields will be populated from TAP candidate data'}
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this template..."
                rows={2}
              />
            </div>

            {/* PDF Upload */}
            <div className="space-y-2">
              <Label>Template File {!showEditDialog && '*'}</Label>
              
              {/* Show existing file when editing */}
              {showEditDialog && selectedTemplate && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border mb-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {selectedTemplate.original_filename || 'Current Template PDF'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {selectedTemplate.onedrive_url ? (
                        <span className="flex items-center gap-1">
                          <Cloud className="w-3 h-3 text-blue-500" />
                          Synced to OneDrive
                        </span>
                      ) : (
                        'Local file only'
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewTemplatePdf(selectedTemplate)}
                    className="gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    View Current
                  </Button>
                </div>
              )}

              {/* File upload input */}
              <div className="space-y-2">
                <Input
                  type="file"
                  accept=".pdf,.docx,.xlsx"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  className="flex-1"
                />
                <p className="text-xs text-slate-500">
                  Supported formats: PDF, Word (.docx), Excel (.xlsx)
                </p>
              </div>
              
              {/* Show newly selected file */}
              {formData.file && (
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 flex-1">
                    New file: {formData.file.name}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handlePreviewNewFile(formData.file)}
                    className="gap-1 text-green-700 hover:text-green-800"
                  >
                    <Eye className="w-3 h-3" />
                    Preview
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setFormData({ ...formData, file: null })}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-slate-500">
                {showEditDialog 
                  ? 'Upload a new file to replace the current template, or leave empty to keep the existing file.'
                  : `Upload a Word (.docx), Excel (.xlsx), or PDF template with placeholders like {{name}}, {{passport_number}}.`
                }
              </p>
            </div>

            {/* Candidate Fields Selection */}
            <div className="space-y-2">
              <Label>Candidate Fields ({formData.target === 'hiring' ? 'Hiring' : 'TAP'})</Label>
              <p className="text-xs text-slate-500 mb-2">
                Select fields to include from {formData.target === 'hiring' ? 'Hiring' : 'TAP'} candidate data
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 bg-slate-50 rounded-lg border">
                {availableFields.map(field => (
                  <div key={field.field} className="flex items-center gap-2">
                    <Checkbox
                      id={field.field}
                      checked={formData.required_fields.includes(field.field)}
                      onCheckedChange={() => handleFieldToggle(field.field)}
                    />
                    <Label htmlFor={field.field} className="text-sm cursor-pointer">
                      {field.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* System Fields Selection */}
            <div className="space-y-2">
              <Label>System Fields</Label>
              <p className="text-xs text-slate-500 mb-2">
                Auto-populated fields like current date, user info, document serial number
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                {systemFields.map(field => (
                  <div key={field.field} className="flex items-center gap-2">
                    <Checkbox
                      id={`sys_${field.field}`}
                      checked={formData.system_fields.includes(field.field)}
                      onCheckedChange={() => handleSystemFieldToggle(field.field)}
                    />
                    <Label htmlFor={`sys_${field.field}`} className="text-sm cursor-pointer">
                      {field.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Fields */}
            <div className="space-y-2">
              <Label>Custom Fields</Label>
              <p className="text-xs text-slate-500 mb-2">
                Add custom fields that will be filled during document generation
              </p>
              
              {/* Existing custom fields */}
              {formData.custom_fields.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.custom_fields.map((field, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-100">
                      <Badge variant="outline">{field.field_name}</Badge>
                      <span className="text-sm flex-1">{field.label}</span>
                      <Badge variant="secondary" className="text-xs">
                        {field.field_type === 'predefined' ? `Options: ${field.predefined_values?.join(', ')}` : 'Text Input'}
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0 text-red-500"
                        onClick={() => handleRemoveCustomField(index)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new custom field */}
              <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Field name (e.g., contract_duration)"
                    value={newCustomField.field_name}
                    onChange={(e) => setNewCustomField({ ...newCustomField, field_name: e.target.value })}
                  />
                  <Input
                    placeholder="Label (e.g., Contract Duration)"
                    value={newCustomField.label}
                    onChange={(e) => setNewCustomField({ ...newCustomField, label: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={newCustomField.field_type}
                    onValueChange={(value) => setNewCustomField({ ...newCustomField, field_type: value })}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text Input</SelectItem>
                      <SelectItem value="predefined">Predefined Values</SelectItem>
                    </SelectContent>
                  </Select>
                  {newCustomField.field_type === 'predefined' && (
                    <Input
                      placeholder="Values (comma-separated)"
                      value={newCustomField.predefined_values}
                      onChange={(e) => setNewCustomField({ ...newCustomField, predefined_values: e.target.value })}
                      className="flex-1"
                    />
                  )}
                  <Button size="sm" onClick={handleAddCustomField} className="gap-1">
                    <Plus className="w-3 h-3" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setShowEditDialog(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleSaveTemplate(showEditDialog)} 
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  {showEditDialog ? 'Update Template' : 'Create Template'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Preview: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description || 'Document template preview'}
            </DialogDescription>
          </DialogHeader>

          <div className="h-[60vh]">
            {selectedTemplate && (
              <iframe
                src={`${API}/document-templates/templates/${selectedTemplate.id}/preview`}
                className="w-full h-full border rounded-lg"
                title="Template Preview"
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={() => window.open(`${API}/document-templates/templates/${selectedTemplate?.id}/preview`, '_blank')}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog open={pdfViewerOpen} onOpenChange={setPdfViewerOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-500" />
              {pdfViewerTitle}
            </DialogTitle>
            <DialogDescription>
              PDF Document Viewer
            </DialogDescription>
          </DialogHeader>

          <div className="h-[70vh] bg-slate-100 rounded-lg overflow-hidden">
            {pdfViewerUrl && (
              <iframe
                src={pdfViewerUrl}
                className="w-full h-full border-0"
                title="PDF Viewer"
              />
            )}
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="outline" onClick={() => setPdfViewerOpen(false)}>
              Close
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => window.open(pdfViewerUrl, '_blank')}
                className="gap-2"
              >
                <Eye className="w-4 h-4" />
                Open in New Tab
              </Button>
              {pdfDownloadUrl && (
                <Button 
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = pdfDownloadUrl;
                    link.download = pdfViewerTitle || 'document.pdf';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandardDocuments;
